import React, { useMemo, Fragment } from "react";
import PropTypes from "prop-types";
import { Map } from "immutable";
import { Detail as TableRowDetail } from "MODULES/shared/components/DetailTable";
import { dollars } from "LIB/formatters/currency";
import { properCase } from "LIB/formatters/text";
import { countryName } from "LIB/countries";
import AuditedNoteTooltip from "MODULES/shared/components/NoteTooltip/AuditedNoteTooltip";
import {
  AUTO_ADJUST_SRP_EXCLUSION,
  BIO_HAZMAT,
  CAGE,
  CLASSIFIED_MEMO,
  COUNTRY_OF_MFG,
  DESCRIPTION,
  DOM_SENSITIVE,
  ESD,
  EUROPEAN_COMMERCE_CONTROL_NUMBER,
  EXPORT_COMPLIANCE,
  HAZMAT,
  HTSUS_CODE,
  IMPORTANT_MEMO,
  LLP,
  MANUFACTURER,
  MLP,
  NATIONAL_STOCK_NUMBER,
  PASS_FAIL,
  PERMANENT_EXTERNAL_NOTE_FOR_OQ,
  PURCHASED_AT,
  SCHEDULE_B_OR_HT_CODE,
  SHELF_LIFE,
  SPECIAL_CONTAINER_FEE,
  SPECIAL_CONTAINER_REQ,
  TOOLING_PART_TYPE,
  TOTAL_CYCLES,
  TOTAL_HOURS,
  UNITED_NATIONS_CODE,
  WARRANTY_OVERRIDE
} from "MODULES/parts/constants";
import Icon from "MODULES/shared/components/Icon/Icon";
import Warnings from "../Search/Warnings";

export default function Table({ part }) {
  const UNCode = part.get(UNITED_NATIONS_CODE);
  const record = useMemo(() => part.toJS(), [part]);

  return (
    <div>
      <table className="table table-condensed parts-details-table">
        <tbody>
          <tr>
            <th className="text-right">Description</th>
            <td>
              {part.get(DESCRIPTION)}{" "}
              {record.description_audit && (
                <AuditedNoteTooltip obj={record} field="description" />
              )}
            </td>
          </tr>
          <tr>
            <th className="text-right">MFG</th>
            <td>{part.get(MANUFACTURER)}</td>
          </tr>
          <tr>
            <th className="text-right">List Price</th>
            <td>{dollars(part.get(MLP), 2, true)}</td>
          </tr>
          <tr>
            <th className="text-right">DOP</th>
            <td>{part.get(PURCHASED_AT)}</td>
          </tr>
          <tr>
            <th className="text-right">Cage</th>
            <td>{part.get(CAGE)}</td>
          </tr>
          <tr>
            <th className="text-right">Ctry of Mfg</th>
            <td>
              {part.get(COUNTRY_OF_MFG) &&
                countryName(part.get(COUNTRY_OF_MFG))}
            </td>
          </tr>
          <TableRowDetail
            label="ECCN"
            value={part.get(EUROPEAN_COMMERCE_CONTROL_NUMBER)}
            renderIfPresent
          />
          <TableRowDetail
            label="UN Code"
            value={UNCode && `UN-${UNCode}`}
            renderIfPresent
          />
          <TableRowDetail
            label="NSN"
            value={part.get(NATIONAL_STOCK_NUMBER)}
            renderIfPresent
          />
          <TableRowDetail
            label="Warnings"
            value={
              [BIO_HAZMAT, HAZMAT, LLP, SHELF_LIFE, EXPORT_COMPLIANCE].some(f =>
                part.get(f)
              ) && <Warnings part={part.toJS()} />
            }
            renderIfPresent
          />
          <TableRowDetail
            label={properCase(TOTAL_HOURS)}
            value={part.get(TOTAL_HOURS)}
            renderIfPresent
          />
          <TableRowDetail
            label={properCase(TOTAL_CYCLES)}
            value={part.get(TOTAL_CYCLES)}
            renderIfPresent
          />
          <TableRowDetail
            label="Container"
            value={
              part.get(SPECIAL_CONTAINER_REQ) && (
                <span className="label label-danger">
                  <i className="fa fa-cube" />
                  &nbsp;&nbsp;SC Req: {dollars(part.get(SPECIAL_CONTAINER_FEE))}
                </span>
              )
            }
            renderIfPresent
          />
          <TableRowDetail
            label="Pass / Fail"
            value={
              part.get(PASS_FAIL) && (
                <span className="label label-primary">
                  <i className="fa fa-graduation-cap" />
                  &nbsp;&nbsp;Pass / Fail
                </span>
              )
            }
            renderIfPresent
          />
          <TableRowDetail
            label={ESD.toUpperCase()}
            value={
              part.get(ESD) && <span className="label label-default">ESD</span>
            }
            renderIfPresent
          />
          <TableRowDetail
            label="DOM Sensitive"
            value={
              part.get(DOM_SENSITIVE) && (
                <span className="label label-primary">
                  <i className="fa fa-calendar" />
                  &nbsp;&nbsp;DOM Sensitive
                </span>
              )
            }
            renderIfPresent
          />
          <TableRowDetail
            label={properCase(WARRANTY_OVERRIDE)}
            value={part.get(WARRANTY_OVERRIDE)}
            renderIfPresent
          />

          <TableRowDetail
            label="Imp Memo"
            value={
              part.get(IMPORTANT_MEMO) && (
                <Fragment>
                  <span style={{ color: "#F44336" }}>
                    {part.get(IMPORTANT_MEMO)}
                  </span>{" "}
                  {record[`${IMPORTANT_MEMO}_audit`] && (
                    <AuditedNoteTooltip obj={record} field={IMPORTANT_MEMO} />
                  )}
                </Fragment>
              )
            }
            renderIfPresent
          />
          <TableRowDetail
            label="Permanent OQ note"
            value={
              part.get(PERMANENT_EXTERNAL_NOTE_FOR_OQ) && (
                <Fragment>
                  {part.get(PERMANENT_EXTERNAL_NOTE_FOR_OQ)}{" "}
                  {record[`${PERMANENT_EXTERNAL_NOTE_FOR_OQ}_audit`] && (
                    <AuditedNoteTooltip
                      obj={record}
                      field={PERMANENT_EXTERNAL_NOTE_FOR_OQ}
                    />
                  )}
                </Fragment>
              )
            }
            renderIfPresent
          />
          <TableRowDetail
            label="Clsfd Memo"
            value={
              part.get(CLASSIFIED_MEMO) && (
                <Fragment>
                  {part.get(CLASSIFIED_MEMO)}{" "}
                  {record[`${CLASSIFIED_MEMO}_audit`] && (
                    <AuditedNoteTooltip obj={record} field={CLASSIFIED_MEMO} />
                  )}
                </Fragment>
              )
            }
            renderIfPresent
          />
          <TableRowDetail
            label="Sched B or HT Code"
            value={part.get(SCHEDULE_B_OR_HT_CODE)}
            renderIfPresent
          />
          <TableRowDetail
            label={properCase(HTSUS_CODE).toUpperCase()}
            value={part.get(HTSUS_CODE)}
            renderIfPresent
          />
          <TableRowDetail
            label="Auto SRP Exclusion"
            value={
              part.get(AUTO_ADJUST_SRP_EXCLUSION) && (
                <span className="text-red">
                  <Icon name="check" />
                </span>
              )
            }
            renderIfPresent
          />
          <TableRowDetail
            label="Part Type"
            value={
              part.get(TOOLING_PART_TYPE) &&
              properCase(part.get(TOOLING_PART_TYPE))
            }
            renderIfPresent
          />
        </tbody>
      </table>
    </div>
  );
}

Table.propTypes = {
  part: PropTypes.instanceOf(Map)
};



// WEBPACK FOOTER //
// ./app/modules/parts/components/Detail/Table.jsx